/**
 * 予約キャンセル処理のモジュール
 * 予約キャンセル時にローカルストレージからも予約データを削除する機能を提供
 */

// ローカルストレージキー（Reactのフックと統一）
const STORAGE_KEY_PREFIX = 'fitspace_booking_';
// 最終更新日時のキーサフィックス
const LAST_UPDATED_SUFFIX = '_last_updated';
// データ有効期限（ミリ秒）: 1時間
const DATA_EXPIRY_MS = 60 * 60 * 1000;

/**
 * 予約キャンセル機能を初期化
 * キャンセルボタンにイベントリスナーを追加
 */
export const initialize = () => {
    // 期限切れデータを全て削除
    cleanupExpiredData();

    // キャンセルボタンを検索（カスタムクラスを使用）
    const cancelButtons = document.querySelectorAll('.js-reservation-cancel');

    if (cancelButtons.length === 0) {
        return;
    }

    for (const button of cancelButtons) {
        const originalUrl = button.getAttribute('href');
        button.addEventListener('click', (e) => {
            // デフォルトの動作をキャンセル
            e.preventDefault();

            // カスタム確認ダイアログを表示
            if (confirm('キャンセルしますか？')) {
                // ボタン自体からデータ属性を取得
                const spaceId = button.dataset.spaceId;
                const startedAt = button.dataset.startedAt;
                const endedAt = button.dataset.endedAt;

                // ローカルストレージから対応する予約を削除
                if (spaceId && (startedAt || endedAt)) {
                    removeReservationFromLocalStorage(spaceId, startedAt, endedAt);
                }

                // 元のリンク先に遷移
                if (button.getAttribute('data-method') === 'delete') {
                    // data-method="delete"の場合はフォームをサブミット
                    submitDeleteForm(originalUrl);
                } else {
                    // 通常のリンクの場合
                    window.location.href = originalUrl;
                }
            }
        });
    }
};

/**
 * 期限切れデータをクリーンアップ
 */
const cleanupExpiredData = () => {
    try {
        // 全てのローカルストレージキーをチェック
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            // 予約データキーのみを処理
            if (key?.startsWith(STORAGE_KEY_PREFIX) && !key.endsWith(LAST_UPDATED_SUFFIX)) {
                const spaceId = key.replace(STORAGE_KEY_PREFIX, '');
                const lastUpdatedKey = `${STORAGE_KEY_PREFIX}${spaceId}${LAST_UPDATED_SUFFIX}`;
                const lastUpdated = localStorage.getItem(lastUpdatedKey);

                // 最終更新日時が存在しない場合はスキップ
                if (!lastUpdated) continue;

                const lastUpdatedTime = Number.parseInt(lastUpdated, 10);
                const now = new Date().getTime();

                // 1時間以上経過していれば削除
                if ((now - lastUpdatedTime) > DATA_EXPIRY_MS) {
                    localStorage.removeItem(key);
                    localStorage.removeItem(lastUpdatedKey);
                }
            }
        }
    } catch (e) {
        console.error('期限切れデータクリーンアップエラー:', e);
    }
};

/**
 * ローカルストレージから予約を削除
 * @param {string} spaceId - スペースID
 * @param {string} startedAt - 予約開始時間
 * @param {string} endedAt - 予約終了時間
 */
const removeReservationFromLocalStorage = (spaceId, startedAt, endedAt) => {
    try {
        if (!spaceId) {
            return;
        }

        if (!startedAt && !endedAt) {
            return;
        }

        // ローカルストレージのキーを生成
        const storageKey = `${STORAGE_KEY_PREFIX}${spaceId}`;
        const savedData = localStorage.getItem(storageKey);
        if (!savedData) {
            return;
        }

        // 保存されている予約を取得
        let reservations;
        try {
            reservations = JSON.parse(savedData);
        } catch (e) {
            console.error('予約データのJSON解析エラー:', e);
            return;
        }

        const originalCount = reservations.length;

        // 開始時間と終了時間で一致する予約を削除
        reservations = reservations.filter(reservation => {
            // 開始時間と終了時間を比較（スネークケースとキャメルケースの両方に対応）
            const reservationStartedAt = reservation.started_at || reservation.startedAt;
            const reservationEndedAt = reservation.ended_at || reservation.endedAt;

            // 時刻のみを抽出して比較
            const getTimePartOnly = (timeStr) => timeStr?.split('T')[1]?.substring(0, 5) || timeStr;

            // 完全一致または時刻部分のみの一致をチェック
            const startMatches =
                reservationStartedAt === startedAt ||
                getTimePartOnly(reservationStartedAt) === getTimePartOnly(startedAt);

            const endMatches =
                reservationEndedAt === endedAt ||
                getTimePartOnly(reservationEndedAt) === getTimePartOnly(endedAt);

            // 開始時間または終了時間のいずれかが一致すれば削除対象
            if ((startedAt && startMatches) || (endedAt && endMatches)) {
                return false; // 削除対象のため残さない
            }

            return true; // 削除対象でないため残す
        });

        // 更新があった場合のみローカルストレージを更新
        if (reservations.length < originalCount) {
            if (reservations.length === 0) {
                // 予約が0件になったら完全に削除
                localStorage.removeItem(storageKey);
                localStorage.removeItem(`${STORAGE_KEY_PREFIX}${spaceId}${LAST_UPDATED_SUFFIX}`);
            } else {
                // 更新日時を記録
                localStorage.setItem(storageKey, JSON.stringify(reservations));
                localStorage.setItem(
                    `${STORAGE_KEY_PREFIX}${spaceId}${LAST_UPDATED_SUFFIX}`,
                    new Date().getTime().toString()
                );
            }
        }
    } catch (e) {
        console.error('ローカルストレージ操作エラー:', e);
    }
};

/**
 * DELETE メソッドのフォームをサブミット
 * @param {string} url - リクエスト先URL
 */
const submitDeleteForm = (url) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.style.display = 'none';

    // CSRFトークンを追加
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);
    }

    // DELETE メソッドを指定
    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'delete';
    form.appendChild(methodInput);

    document.body.appendChild(form);
    form.submit();
};